import { z } from 'zod';
import { apiGet, apiSend } from '@/lib/api';

/**
 * ODSTĘPY ODPYTYWANIA TABEL SPS.
 *
 * ⚠️ `everySec: null` ZNACZY „PRZY KAŻDYM CYKLU" i jest stanem domyślnym — wyspa bez ani jednego
 * ustawienia zachowuje się dokładnie tak, jak przed wprowadzeniem tego ekranu.
 */
export const SyncTableSchema = z.object({
  sps: z.string(),
  everySec: z.number().nullable(),
  /** Tabele, bez których wiersze tej tabeli byłyby sierotami — agent dociąga je sam. */
  wymaga: z.array(z.string()),
  /** Ile wierszy trzyma read-model. `null` = nie liczymy dla tej tabeli. */
  wierszy: z.number().nullable(),
  /** `true` = czytana kursorem (przyrostowo), więc krótki odstęp jest tani. */
  kursorowa: z.boolean(),
  updatedAt: z.string().nullable(),
  updatedByEmail: z.string().nullable(),
  /**
   * ZMIERZONY NARZUT ODCZYTU (ms) — średnia z ostatniej godziny pracy agenta.
   *
   * ⚠️ TWARDA LICZBA OBOK `wierszy`, KTÓRE SĄ TYLKO PRZYBLIŻENIEM: liczba wierszy mówi, ile tego
   * jest u nas, a ten czas — ile to kosztuje NA MASZYNIE KLIENTA. `null` = agent jeszcze nie
   * przysłał pomiaru (średnie jadą raz na godzinę), i wtedy pokazujemy „—", nie zero.
   */
  narzutMs: z.number().nullable(),
  narzutPomiarow: z.number().nullable(),
});

export type SyncTable = z.infer<typeof SyncTableSchema>;

/**
 * GRUPA TABEL — jednostka, na ktorej ustawia sie odstep.
 *
 * ⚠️ SKLAD GRUP I ICH CENA PRZYCHODZA Z PORTALU, nie stoja tutaj. Ta wiedza wynika z grafu
 * zaleznosci read-modelu (`api/src/sync-tables/grupy-tabel.ts`), a druga kopia w kliencie
 * rozjechalaby sie po cichu przy pierwszej nowej tabeli — dokladnie tak samo, jak lista zaleznosci
 * nie stoi w agencie.
 */
const GrupaSchema = z.object({
  klucz: z.enum(['ludzie', 'asortyment', 'planogram', 'uprawnienia']),
  nazwa: z.string(),
  opis: z.string(),
  tabele: z.array(SyncTableSchema),
  /** Odstep grupy w CYKLACH agenta; `null` = w kazdym cyklu. */
  cykli: z.number().nullable(),
  /** `true` = tabele grupy maja rozne odstepy (stan sprzed grupowania) — ekran musi to powiedziec. */
  mieszane: z.boolean(),
  wierszyRazem: z.number().nullable(),
  narzutMsRazem: z.number().nullable(),
  /** Tabele tej grupy dociagane przez INNE grupy — beda czytane czesciej, niz mowi odstep grupy. */
  dociagane: z.array(z.object({ tabela: z.string(), przezGrupy: z.array(z.string()) })),
});

export type GrupaTabel = z.infer<typeof GrupaSchema>;

const SyncTablesSchema = z.object({
  rows: z.array(SyncTableSchema),
  grupy: z.array(GrupaSchema),
  /** Odstęp cyklu agenta — dolna granica wszystkiego, co widać na tym ekranie. */
  cyklSek: z.number(),
  /** Średni czas CAŁEJ fazy odczytu tabel; `null` = brak pomiaru z agentów. */
  odczytMs: z.number().nullable(),
  /** Średni czas całego cyklu — górna granica dla powyższego. */
  cyklMs: z.number().nullable(),
});

export const fetchSyncTables = (groupId: string) => apiGet(`/sync-tables?groupId=${groupId}`, SyncTablesSchema);

/**
 * ODSTEP CALEJ GRUPY, w CYKLACH agenta.
 *
 * ⚠️ CYKLE, NIE SEKUNDY: agent czyta tabele tylko w cyklu, wiec sekundy pozwalaly wpisac liczbe,
 * ktora brzmi precyzyjnie i nie ma pokrycia w zachowaniu (80 s przy cyklu 60 s = co drugi cykl).
 */
/**
 * DLUGOSC CYKLU AGENTA na tej wyspie, w sekundach.
 *
 * ⚠️ 30–600 s. Granice maja uzasadnienie w innych liczbach systemu (koszt pelnego odczytu u dolu,
 * prog „agent milczy" u gory) — stoi ono przy `ustawCyklSek` po stronie API.
 */
export const setCyklSek = (groupId: string, cyklSek: number) =>
  apiSend('PUT', '/sync-tables/cykl', { groupId, cyklSek }, z.object({ cyklSek: z.number() }));

export const setGrupaCykli = (klucz: string, groupId: string, cykli: number | null) =>
  apiSend(
    'PUT',
    `/sync-tables/grupa/${klucz}`,
    { groupId, cykli },
    z.object({ grupa: z.string(), cykli: z.number().nullable(), tabel: z.number() }),
  );

export const setSyncTableInterval = (table: string, groupId: string, everySec: number | null) =>
  apiSend('PUT', `/sync-tables/${table}`, { groupId, everySec }, z.object({ sps: z.string(), everySec: z.number().nullable() }));

/**
 * ODSTĘP SPISU KOPII — co ile CYKLI agent liczy zawartość katalogu i wolne miejsce.
 *
 * ⚠️ Cykle, nie sekundy: spis dzieje się wewnątrz cyklu agenta, a jego długość ustawia się
 * lokalnie na maszynie (`AGENT_INTERVAL_SEC`) i portal jej nie zna.
 */
export const fetchOdstepKopii = (groupId: string) =>
  apiGet(`/sync-tables/kopie/odstep?groupId=${encodeURIComponent(groupId)}`, z.object({ coCykli: z.number() }));

export const setOdstepKopii = (groupId: string, coCykli: number) =>
  apiSend('PUT', '/sync-tables/kopie/odstep', { groupId, coCykli }, z.object({ coCykli: z.number() }));

/**
 * PRÓG RUCHU AGENTA NA DOBĘ (MB). `0` = bez alarmu.
 *
 * ⚠️ ISTNIEJE PO AWARII U KLIENTA: agent na LTE zjadł pakiet 150 GB w dwa tygodnie i nikt tego nie
 * widział. Zwykła praca to ~32 MB na dobę (zmierzone), więc próg w setkach MB odzywa się przy
 * awarii i milczy przy normalnej pracy, także z kopiami bazy.
 */
/**
 * KOMUNIKAT TRYBU KONSERWACJI — tekst na czarnym ekranie automatu, per wyspa.
 *
 * ⚠️ `domyslny` przychodzi z serwera po to, zeby pole mialo go jako PODPOWIEDZ. Puste pole znaczy
 * „wroc do domyslnego", a nie „pokaz pusty ekran" — i czlowiek musi widziec, co wtedy zobaczy
 * ktos przy automacie.
 */
export const fetchKomunikatKonserwacji = (groupId: string) =>
  apiGet(
    `/islands/${groupId}/konserwacja-komunikat`,
    z.object({ komunikat: z.string().nullable(), domyslny: z.string() }),
  );

export const setKomunikatKonserwacji = (groupId: string, komunikat: string | null) =>
  apiSend('PUT', `/islands/${groupId}/konserwacja-komunikat`, { komunikat }, z.object({ komunikat: z.string().nullable() }));

export const fetchLimitRuchu = (groupId: string) =>
  apiGet(`/sync-tables/limit-ruchu?groupId=${encodeURIComponent(groupId)}`, z.object({ limitMb: z.number() }));

export const setLimitRuchu = (groupId: string, limitMb: number) =>
  apiSend('PUT', '/sync-tables/limit-ruchu', { groupId, limitMb }, z.object({ limitMb: z.number() }));

/**
 * Prośba o świeży spis — wołana przy wejściu na zakładkę kopii.
 *
 * ⚠️ Skutek przychodzi po DWÓCH cyklach, nie od razu: spis jedzie w sygnale życia agenta,
 * a ta prośba wraca dopiero w odpowiedzi na niego.
 */
export const odswiezSpisKopii = (groupId: string) =>
  apiSend('PUT', '/sync-tables/kopie/odswiez', { groupId }, z.object({ poproszono: z.boolean(), zaIleCykli: z.number() }));

/**
 * KOREKTA ZEGARA MASZYN TEJ WYSPY.
 *
 * ⚠️ Przeniesione z `agent.json` na maszynie. Włączenie wymagało dotąd wejścia na maszynę klienta
 * — czyli na tę, na którą wchodzi się najrzadziej, a to właśnie tam zegar odjeżdża.
 */
/**
 * ⚠️ PRÓG JEDZIE RAZEM ZE ZGODĄ, bo to jedna decyzja w dwóch połowach: „czy korygować" i „od
 * kiedy". Do 0.3.396 portal rozstrzygał tylko pierwszą, a druga siedziała w `agent.json` na
 * maszynie klienta — czyli ta ważniejsza wymagała wejścia na maszynę.
 */
const ZegarSchema = z.object({ naprawiaj: z.boolean(), progSekund: z.number() });

export const fetchZegarNaprawiaj = (groupId: string) =>
  apiGet(`/sync-tables/zegar?groupId=${encodeURIComponent(groupId)}`, ZegarSchema);

/** `progSekund` pominięty = nie ruszaj progu (przełącznik nie ma prawa nadpisać go nieświeżą wartością). */
export const setZegarNaprawiaj = (groupId: string, naprawiaj: boolean, progSekund?: number) =>
  apiSend(
    'PUT',
    '/sync-tables/zegar',
    { groupId, naprawiaj, ...(progSekund === undefined ? {} : { progSekund }) },
    ZegarSchema,
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  „PRZEMIEL TO JESZCZE RAZ" — prośba do węzłów o ponowny PEŁNY ODCZYT tabel.
 *
 *  ⚠️⚠️ TO NIE JEST ZAPIS DO MASZYNY. Portal kasuje wyłącznie swój znacznik „ten węzeł już mi tę
 *  tabelę dostarczył", więc agent przy najbliższym meldunku przyśle ją w CAŁOŚCI zamiast samych
 *  zmienionych wierszy. W bazie klienta nie zmienia się ani jeden bajt.
 *
 *  ⚠️ POTRZEBNE, BO ODRZUCONEGO WIERSZA AGENT SAM NIE PONOWI: portal odpowiada `2xx` także wtedy,
 *  gdy wiersz odsieje, więc agent zapisuje „przyjęte" i wraca do niego dopiero, gdy ktoś fizycznie
 *  ruszy towar w tej przegródce.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const poprosOPelnyOdczyt = (groupId: string, tabele: string[], nodeId: string | null) =>
  apiSend(
    'POST',
    `/islands/${groupId}/pelny-odczyt`,
    { tabele, nodeId },
    z.object({ tabele: z.array(z.string()), wezly: z.array(z.string()), skasowanych: z.number() }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PRZERWIJ TRWAJĄCY PEŁNY ODCZYT — odwrotność `poprosOPelnyOdczyt`.
 *
 *  ⚠️⚠️ PROŚBA O PEŁNY ODCZYT JEST STANEM, NIE ZDARZENIEM: dopóki trwa, agent czyta całe tabele
 *  w KAŻDYM cyklu. Na wyspie z 7 tys. pracowników (21.09.2026) zalało to kolejkę, a zatrzymać
 *  się dało wyłącznie zapytaniem do bazy instancji — czyli nie dało się nikomu, kto ma sam portal.
 *
 *  ⚠️ PORTAL ZAPISZE „DOSTARCZONE" MIMO NIEDOKOŃCZONEGO ODCZYTU. Ekran ma o tym mówić wprost,
 *  bo to świadoma decyzja człowieka, a nie szczegół techniczny.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const przerwijPelnyOdczyt = (groupId: string, tabele: string[], nodeId: string | null) =>
  apiSend(
    'POST',
    `/islands/${groupId}/pelny-odczyt/przerwij`,
    { tabele, nodeId },
    z.object({ tabele: z.array(z.string()), wezly: z.array(z.string()), zamknietych: z.number() }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  STAN SYNCHRONIZACJI WSZYSTKICH TABEL — portal kontra węzły.
 *
 *  ⚠️⚠️ ZAMÓWIONE 21.09.2026: „chciałem sprawdzać stan synchronizacji WSZYSTKICH tabel, a nie
 *  tylko drawersów". Nic tu nie pytamy maszyn — obie strony porównania leżą już w bazie portalu.
 *
 *  ⚠️ `NIE_WIEMY` TO TRZECI STAN, nie zgoda. Czujnik obejmuje wyłącznie tabele per automat;
 *  pokazanie reszty jako zgodnej byłoby tym samym błędem, przez który ekran rozjazdu przez trzy
 *  dni uspokajał, porównując węzły wyłącznie ze sobą.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const StanTabelSchema = z.object({
  tabele: z.array(
    z.object({
      sps: z.string(),
      portal: z.number().nullable(),
      mierzona: z.boolean(),
      wezly: z.array(z.object({ nodeId: z.string(), wierszy: z.number() })),
      rozjechane: z.array(z.string()),
      /**
       * ⚠️⚠️ `PODZBIOR` = portal ŚWIADOMIE trzyma część tej tabeli (biała lista parametrów), więc
       * różnica liczb NIC nie znaczy. Pokazanie tego jako rozjazdu zapraszałoby do „naprawy",
       * która wciągnęłaby do portalu tysiące wierszy, o które nikt nie prosi.
       */
      stan: z.enum(['ZGODNE', 'ROZJAZD', 'NIE_WIEMY', 'PODZBIOR', 'ZNANA_ROZNICA']),
      podzbior: z.string().nullable(),
      /**
       * ⚠️ `optional` przy trzech nowych polach: starsza instancja ich nie odda, a brak ma znaczyć
       * „bez przycisków”, a nie wywrócić cały panel stanu tabel.
       *
       * `roznica` — o ile bazy różnią się od portalu, gdy każda rozjechana o tyle samo (tylko taką
       * da się uznać); `znana` — zapisane uznanie; `odczyt` — jak odpytać tabelę wiersz po wierszu.
       */
      roznica: z.number().nullable().optional(),
      znana: z.object({ roznica: z.number(), kto: z.string(), kiedy: z.string() }).nullable().optional(),
      odczyt: z.enum(['WYSPA', 'AUTOMAT']).nullable().optional(),
      /**
       * ⚠️⚠️ SKĄD POCHODZI LICZBA WĘZŁA. `CZUJNIK` jedzie co cykl i obejmuje tylko tabele per
       * automat; `ODCZYT` to spis na żądanie i obejmuje wszystko. Bez tego pola dwie liczby
       * z dwóch różnych chwil stałyby obok siebie bez słowa, a ich różnica nazywałaby się rozjazdem.
       */
      zrodlo: z.enum(['ODCZYT', 'CZUJNIK']).nullable(),
    }),
  ),
  mierzonych: z.number(),
  spisKiedy: z.string().nullable(),
  spisCzeka: z.boolean(),
});

export const fetchStanTabel = (groupId: string) =>
  apiGet(`/sync-tables/stan/tabel?groupId=${encodeURIComponent(groupId)}`, StanTabelSchema);

/** Uznanie różnicy za wyjaśnioną — portal sprawdza ją na świeżym stanie, patrz serwis. */
export const ustawZnanaRoznice = (groupId: string, tabela: string, roznica: number) =>
  apiSend(
    'POST',
    '/sync-tables/stan/znana-roznica',
    { groupId, tabela, roznica },
    z.object({ tabela: z.string(), roznica: z.number() }),
  );

export const usunZnanaRoznice = (groupId: string, tabela: string) =>
  apiSend('POST', '/sync-tables/stan/znana-roznica/usun', { groupId, tabela }, z.object({ usunieto: z.number() }));

export const zlecSpisTabel = (groupId: string) =>
  apiSend(
    'POST',
    '/sync-tables/stan/spis',
    { groupId },
    z.object({ batchId: z.string(), wezly: z.array(z.string()), zaStare: z.array(z.string()) }),
  );
